<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import KanbanListColumnCell from './KanbanListColumnCell.vue';
import KanbanListAddColumnMenu from './KanbanListAddColumnMenu.vue';
import { statusColorClasses } from 'dashboard/modules/kanban/constants/statusByCategory';

const props = defineProps({
  stage: { type: Object, required: true }, // { id, name, column_type, position }
  cards: { type: Array, default: () => [] },
  columns: { type: Array, required: true }, // colunas visíveis, na ordem
  hiddenColumns: { type: Array, required: true },
  pipelineCategory: { type: String, default: 'sales' },
});

const emit = defineEmits([
  'cardClick',
  'reorderColumn',
  'hideColumn',
  'addColumn',
]);

function statusBarClass(card) {
  return statusColorClasses(props.pipelineCategory, card.status ?? 'open').bar;
}

const { t } = useI18n();
const isCollapsed = ref(false);

const totalValue = computed(() => {
  const sum = props.cards.reduce((acc, card) => {
    const v = card.value;
    return v !== null && v !== undefined && v !== '' ? acc + Number(v) : acc;
  }, 0);
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  }).format(sum);
});

const columnLabel = col => (col.isCustom ? col.label : t(col.labelKey));

// ── drag-to-reorder via clicar e segurar no th ──────────────────────
const draggingKey = ref(null);

function onHeaderMousedown(key) {
  draggingKey.value = key;
}

function onHeaderMouseenter(targetKey) {
  if (!draggingKey.value || draggingKey.value === targetKey) return;
  const targetIndex = props.columns.findIndex(c => c.key === targetKey);
  emit('reorderColumn', { key: draggingKey.value, newIndex: targetIndex });
}

function onHeaderMouseup() {
  draggingKey.value = null;
}
</script>

<template>
  <div class="border-b border-n-weak">
    <button
      class="w-full flex items-center gap-2 px-4 py-2.5 bg-n-alpha-1 sticky top-0 z-10 text-left"
      @click="isCollapsed = !isCollapsed"
    >
      <span
        class="i-lucide-chevron-down size-4 text-n-slate-10 transition-transform"
        :class="{ '-rotate-90': isCollapsed }"
      />
      <span class="font-medium text-n-slate-12">{{ stage.name }}</span>
      <span class="text-sm text-n-slate-10">{{ cards.length }}</span>
      <span class="text-sm text-n-slate-10 ml-auto">{{ totalValue }}</span>
    </button>

    <div v-if="!isCollapsed" class="overflow-x-auto">
      <table
        class="w-full text-sm"
        @mouseup="onHeaderMouseup"
        @mouseleave="onHeaderMouseup"
      >
        <thead>
          <tr class="text-left text-n-slate-11 border-b border-n-weak">
            <th class="w-1 p-0" />
            <th
              v-for="col in columns"
              :key="col.key"
              class="group px-4 py-2 font-medium whitespace-nowrap select-none cursor-grab active:cursor-grabbing"
              @mousedown="onHeaderMousedown(col.key)"
              @mouseenter="onHeaderMouseenter(col.key)"
            >
              <span class="inline-flex items-center gap-1">
                {{ columnLabel(col) }}
                <span
                  class="i-lucide-x size-3 opacity-0 group-hover:opacity-100 text-n-slate-10 hover:text-n-slate-12 transition-opacity"
                  :title="t('KANBAN.LIST_VIEW.REMOVE_COLUMN')"
                  @mousedown.stop
                  @click.stop="emit('hideColumn', col.key)"
                />
              </span>
            </th>
            <th class="px-2 py-2 w-10">
              <KanbanListAddColumnMenu
                :hidden-columns="hiddenColumns"
                @add="key => emit('addColumn', key)"
              />
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(card, idx) in cards"
            :key="card.id"
            class="border-b border-n-weak cursor-pointer hover:bg-n-alpha-2 transition-colors"
            :class="idx % 2 === 1 ? 'bg-n-alpha-1' : ''"
            @click="emit('cardClick', card)"
          >
            <td class="w-1 p-0">
              <span
                class="block w-1 h-full min-h-[2.75rem] rounded-sm"
                :class="statusBarClass(card)"
              />
            </td>
            <td
              v-for="col in columns"
              :key="col.key"
              class="px-4 py-3"
              :class="col.key === 'tags' ? 'max-w-[480px]' : 'max-w-[240px]'"
            >
              <KanbanListColumnCell :card="card" :column="col" />
            </td>
            <td />
          </tr>
        </tbody>
      </table>

      <p v-if="!cards.length" class="text-center text-sm text-n-slate-10 py-6">
        {{ t('KANBAN.LIST_VIEW.EMPTY_SECTION') }}
      </p>
    </div>
  </div>
</template>
